import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { ResultadoDaJogada, RITMO } from './ResultadoDaJogada'
import { DICE_IMAGES } from '@/types/dice'

// Importar estilos específicos para dados compartilhados
import '@/styles/shared-dice-roll.css'

/**
 * A cerimônia da rolagem: a tela que a MESA TODA vê.
 *
 * Reescrita em 2026-07-25 a partir de um relato de uso do Paulo: "a rolagem é
 * rápida demais, não dá para ver o resultado de cada dado nem a conta que
 * levou ao resultado".
 *
 * O diagnóstico não era velocidade. Esta tela nasceu para UM dado — mostrava
 * uma imagem e um número — e nunca aprendeu a desenhar uma bandeja. Numa
 * jogada com vários dados ela não mostrava nem a imagem (não existe figura
 * para "POOL") e exibia só o total: um resultado sem a conta.
 *
 * Agora ela desenha a jogada inteira, na mesma anatomia da bandeja e do balão
 * do chat — a jogada tem uma cara só, apareça onde aparecer:
 *
 *   1. quem rolou          4. a conta por extenso
 *   2. a notação           5. o total
 *   3. cada dado           6. o veredito (quando há dificuldade)
 *
 * E o ritmo tem três tempos, em vez de um instante:
 *
 *   girando   -> os dados rolam; ninguém precisa ler nada
 *   assentou  -> cada dado para mostrando a face que tirou
 *   resolveu  -> a regra ACONTECE à vista: os descartados apagam, as parcelas
 *                entram e o total aparece
 *
 * Ver a regra acontecer é o ponto pedagógico: "manter o maior" só ensina
 * alguma coisa se der para ver o dado que ficou de fora.
 *
 * A tela então CONGELA. Ela não some por cronômetro: sai quando a jogada vira
 * registro no chat — porque aí o resultado passou a existir num lugar
 * permanente. Quem rolou tem o botão de enviar; se não tocar, o envio sai
 * sozinho, para ninguém prender a tela da turma inteira.
 */

interface SharedDiceRollProps {
  isActive: boolean
  player: string
  diceType: string
  startTime: number
  userId: string
  characterName?: string
  result?: number | string
  isAnimating?: boolean
  className?: string
  /** Id da rolagem: quem rolou envia ao chat daqui mesmo. */
  rollId?: string
  /** A jogada inteira (ausente na cerimônia de sorteio e em rolagens antigas). */
  diceSpec?: { type: string; result: number }[]
  kept?: number[]
  rule?: string | null
  expression?: string | null
  modifiers?: { id: string; name: string; value: number }[]
  attributeName?: string
  attributeValue?: number
  bonus?: number
  total?: number
  difficulty?: number
  outcome?: 'success' | 'failure'
  /** Quem está vendo é quem rolou? Ele envia pela própria bandeja. */
  souEuQuemRolou?: boolean
  /** Quem está vendo é o mestre? Ele pode destravar uma jogada parada. */
  souMestre?: boolean
  /** Envia a jogada ao chat. Recebe o id da rolagem. */
  onEnviar?: (rollId: string) => Promise<boolean>
}

export function SharedDiceRoll({
  isActive,
  player,
  diceType,
  startTime,
  characterName,
  result,
  isAnimating = true,
  className,
  rollId,
  diceSpec,
  kept,
  rule,
  expression,
  modifiers,
  attributeName,
  attributeValue,
  bonus,
  total,
  difficulty,
  outcome,
  souEuQuemRolou = false,
  souMestre = false,
  onEnviar,
}: SharedDiceRollProps) {
  const [fase, setFase] = useState<'girando' | 'assentou' | 'resolveu'>('girando')
  const [enviando, setEnviando] = useState(false)
  // O resultado chega quase junto com o inicio (o servidor responde em ~100ms).
  // Segurar aqui e o que da tempo de VER os dados girarem — para quem assiste
  // vale a mesma espera de quem rolou.
  const [podeMostrar, setPodeMostrar] = useState(false)
  const desdeQuandoGira = useRef<number | null>(null)

  const temResultado = result !== undefined && podeMostrar
  const isSorteio = diceType === 'sorteio'

  // Uma jogada de UM dado tambem e desenhada como bandeja de um cubo: sem
  // isto o dado avulso cairia numa terceira exibicao (a imagem antiga), que e
  // exatamente como este modulo ja se perdeu tres vezes.
  const specDaJogada = isSorteio
    ? null
    : (diceSpec?.length
        ? diceSpec
        : [{ type: diceType, result: typeof result === 'number' ? result : 0 }])
  const temBandeja = !!specDaJogada

  // Marca quando esta cerimônia começou, para o chacoalho ter duração humana
  // e não a da requisição.
  useEffect(() => {
    if (!isActive) {
      desdeQuandoGira.current = null
      setPodeMostrar(false)
      return
    }
    if (desdeQuandoGira.current === null) desdeQuandoGira.current = Date.now()
  }, [isActive])

  useEffect(() => {
    if (!isActive || result === undefined || podeMostrar) return
    const jaGirou = Date.now() - (desdeQuandoGira.current ?? Date.now())
    const falta = Math.max(0, RITMO.chacoalho - jaGirou)
    const t = setTimeout(() => setPodeMostrar(true), falta)
    return () => clearTimeout(t)
  }, [isActive, result, podeMostrar])

  // Três tempos. O passo "assentou -> resolveu" existe para a regra acontecer
  // À VISTA, e não já resolvida quando os dados param.
  useEffect(() => {
    if (!isActive) return
    if (!temResultado) {
      setFase('girando')
      return
    }
    setFase('assentou')
    const t = setTimeout(() => setFase('resolveu'), RITMO.assentar + RITMO.resolver)
    return () => clearTimeout(t)
  }, [isActive, temResultado])

  if (!isActive) return null

  const displayName = characterName ? `${player} (${characterName})` : player
  const mantidos = new Set(kept ?? [])
  const resolveu = fase === 'resolveu'
  const contouSucessos = rule === 'sucessos'

  // Sorteio de personagem: mesma dinâmica, moldura de ritual, sem bandeja.
  const diceImage = isSorteio
    ? DICE_IMAGES.D20
    : DICE_IMAGES[diceType as keyof typeof DICE_IMAGES]

  const parcelas = [
    ...(modifiers ?? []).map(m => ({ nome: m.name, valor: m.value })),
    ...(attributeName && attributeValue !== undefined
      ? [{ nome: attributeName, valor: attributeValue }]
      : []),
    ...(bonus ? [{ nome: 'bônus', valor: bonus }] : []),
  ]

  const enviar = async () => {
    if (!rollId || !onEnviar || enviando) return
    setEnviando(true)
    const foi = await onEnviar(rollId)
    if (!foi) setEnviando(false)
  }

  return (
    <div className={cn(
      'fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4',
      'animate-fade-in-dice',
      className
    )}>
      <div
        className={cn(
          'shared-dice-container w-full max-w-md',
          resolveu && result === 20 && diceType === 'D20' && 'shared-dice-critical-success',
          resolveu && result === 1 && diceType === 'D20' && 'shared-dice-critical-failure'
        )}
        data-testid="cerimonia-dados"
      >
        <div className="shared-dice-header">
          <h3 className="shared-dice-title">
            {isSorteio ? 'Sorteio do destino' : 'Rolagem de dados'}
          </h3>
          <p className="shared-dice-player">
            {isSorteio
              ? `Sorteando o destino de ${displayName}...`
              : displayName}
          </p>
          {/* 2. a notação: o que foi montado */}
          {expression && (
            <p
              className="font-mono text-xs opacity-70 mt-1 break-words"
              data-testid="cerimonia-notacao"
            >
              {expression}
            </p>
          )}
        </div>

        <div className="shared-dice-area">
          {/* 3. cada dado com seu número — os descartados apagam quando a
              regra é aplicada, e continuam legíveis */}
          {temBandeja ? (
            // MESMO componente da bandeja: a jogada tem uma cara so, esteja na
            // tela de quem rolou ou na de quem assiste.
            <ResultadoDaJogada
              jogada={{
                spec: specDaJogada!,
                kept,
                rule,
                expression,
                modifiers,
                attributeName,
                attributeValue,
                bonus,
                result: typeof result === 'number' ? result : undefined,
                total,
                difficulty,
                outcome,
              }}
              rolando={!temResultado}
              resolvido={resolveu}
              tamanhoDoDado={60}
              variante="escura"
            />
          ) : (
            // Sorteio de personagem: nao e jogada de dado, e ritual pre-jogo —
            // segue com a imagem e o papel revelado.
            <div className={cn('shared-dice-image-container', `dice-${diceType.toLowerCase()}`)}>
              {diceImage && (
                <img
                  src={diceImage}
                  alt={diceType}
                  className={cn(
                    'shared-dice-image',
                    !temResultado && isAnimating && 'motion-safe:animate-bounce-rotate'
                  )}
                />
              )}
            </div>
          )}

          <div className="shared-dice-status">
            {!temResultado && (
              <div className="shared-dice-rolling">
                <div className="shared-dice-rolling-indicator animate-pulse-dice"></div>
                <span className="shared-dice-rolling-text">Rolando...</span>
              </div>
            )}

            {temResultado && isSorteio && (
              <div className="shared-dice-result">
                <div className="shared-dice-result-value">{`Papel: ${result}`}</div>
                <div className="shared-dice-result-details">
                  {`${displayName} recebeu seu papel`}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* A tela congela aqui. Quem rolou fecha o momento enviando; os outros
            só assistem e a tela sai sozinha quando a jogada chega ao chat. */}
        {temResultado && !isSorteio && (
          <div className="flex flex-col items-center gap-2 pt-2">
            <span className="text-xs text-white/50">
              {`Aguardando ${player} enviar ao chat...`}
            </span>
            {/* Destravamento do mestre: se o jogador sair da sala com a jogada
                parada, a mesa nao fica esperando para sempre. E empurrar, nao
                vetar — ninguem pode impedir uma jogada de virar registro. */}
            {souMestre && rollId && onEnviar && (
              <button
                type="button"
                onClick={enviar}
                disabled={enviando}
                data-testid="cerimonia-enviar-pelo-mestre"
                className={cn(
                  'min-h-11 px-4 rounded-xl text-sm font-medium',
                  'border border-white/30 text-white/80 active:translate-y-px',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {enviando ? 'Enviando...' : `Enviar por ${player}`}
              </button>
            )}
          </div>
        )}

        {!temResultado && (
          <div className="shared-dice-progress">
            <div className="shared-dice-progress-bar">
              <div
                className="shared-dice-progress-fill"
                style={{ width: `${Math.min(100, ((Date.now() - startTime) / 3000) * 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SharedDiceRoll
